import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import api from "../services/api";
import refreshToken from "../utils/refreshToken";
import { SCHEDULE_SELECTED_TEAM_STORAGE_KEY } from "../constants/scheduleTeamStorage";
import {
  TeamListItem,
  unwrapTeamsList,
} from "../pages/Teams/teamApi";
import { roleUsesScheduleTeams } from "../utils/scheduleTeamRoles";
import {
  pickScheduleTeamId,
  readCachedScheduleTeams,
  writeCachedScheduleTeams,
} from "../utils/scheduleTeamCache";
import { useAuth } from "./auth";

interface ScheduleTeamContextValue {
  myTeams: TeamListItem[];
  selectedTeamId: number | null;
  setSelectedTeamId: (id: number | null) => void;
  teamsFetchDone: boolean;
  usesTeamSchedules: boolean;
}

const ScheduleTeamContext = createContext<ScheduleTeamContextValue | null>(
  null
);

export function ScheduleTeamProvider({ children }: { children: ReactNode }) {
  const { user, access_token, dateToExpires } = useAuth();
  const [myTeams, setMyTeams] = useState<TeamListItem[]>([]);
  const [selectedTeamId, setSelectedTeamIdState] = useState<number | null>(
    null
  );
  const [teamsFetchDone, setTeamsFetchDone] = useState(false);

  const usesTeamSchedules = roleUsesScheduleTeams(user?.role);

  useEffect(() => {
    if (!user) {
      setMyTeams([]);
      setSelectedTeamIdState(null);
      setTeamsFetchDone(false);
      return;
    }

    if (!usesTeamSchedules) {
      setMyTeams([]);
      setSelectedTeamIdState(null);
      setTeamsFetchDone(true);
      return;
    }

    let cancelled = false;
    const userId = Number(user.id);
    const savedRaw = localStorage.getItem(SCHEDULE_SELECTED_TEAM_STORAGE_KEY);
    const savedId = savedRaw && Number.isFinite(Number(savedRaw)) ? Number(savedRaw) : null;

    // With a cached list the agenda starts right away; /teams only confirms it.
    const cachedTeams = readCachedScheduleTeams(userId);
    if (cachedTeams) {
      setMyTeams(cachedTeams);
      setSelectedTeamIdState((prev) => pickScheduleTeamId(cachedTeams, prev, savedId));
      setTeamsFetchDone(true);
    } else {
      setTeamsFetchDone(false);
    }

    (async () => {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
        const res = await api.get("/api/adm/teams");
        const teams = unwrapTeamsList(res);
        if (cancelled) return;
        writeCachedScheduleTeams(userId, teams);
        setMyTeams(teams);
        setSelectedTeamIdState((prev) => pickScheduleTeamId(teams, prev, savedId));
      } catch {
        // Keep the cached teams when the refresh fails.
        if (!cancelled && !cachedTeams) {
          setMyTeams([]);
          setSelectedTeamIdState(null);
        }
      } finally {
        if (!cancelled) setTeamsFetchDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, usesTeamSchedules, access_token, dateToExpires]);

  const setSelectedTeamId = useCallback((id: number | null) => {
    setSelectedTeamIdState(id);
    if (id != null) {
      localStorage.setItem(
        SCHEDULE_SELECTED_TEAM_STORAGE_KEY,
        String(id)
      );
    }
  }, []);

  const value: ScheduleTeamContextValue = {
    myTeams,
    selectedTeamId,
    setSelectedTeamId,
    teamsFetchDone,
    usesTeamSchedules,
  };

  return (
    <ScheduleTeamContext.Provider value={value}>
      {children}
    </ScheduleTeamContext.Provider>
  );
}

export function useScheduleTeam(): ScheduleTeamContextValue {
  const ctx = useContext(ScheduleTeamContext);
  if (!ctx) {
    throw new Error("useScheduleTeam must be used within ScheduleTeamProvider");
  }
  return ctx;
}
